// Work Streams (brain: work-streams) — one tab bar across every Dev Console
// the signed-in person can reach, on any org, any instance. Each tab is a
// compact native pane built from the instance's /dev API over the plane's
// deploy-key tether (GET /streams, POST /streams/:id/dev): a pulse strip,
// the latest (or pinned) session's feed with plan approval and the
// questions form, a composer with dictation, the Ship strip, and the
// 90-second SSO link into the full console for anything deeper. Built for
// a phone first: one pane on screen, swipe or tap the tabs to flick between
// 6–10 parallel efforts.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { apiGet, apiPost, isBackendConfigured } from '../utils/backendApi';
import { useVoiceSessionOnly } from '../hooks/VoiceSessionProvider';
import './Streams.css';

const LAST_STREAM = 'nventr.streams.active';
const COLORS = ['#14e0c8', '#4f8cff', '#e8b34b', '#e5484d', '#a78bfa', '#3ecf8e', '#f472b6', '#f97316'];
const LIVE = new Set(['starting', 'running', 'cancelling', 'queued']);
const KIND_LABEL = { instance: 'Instance', repo: 'Repo', lane: 'Lane', builder: 'App Builder' };

const fmtAgo = (ms) => {
  if (!ms) return '';
  const s = Math.max(0, (Date.now() - ms) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
};
const clip = (s, n) => (String(s || '').length > n ? `${String(s).slice(0, n - 1)}…` : String(s || ''));
const visible = () => typeof document === 'undefined' || document.visibilityState !== 'hidden';

// Poll while the tab is visible; `ms` can change between ticks.
function useVisiblePoll(fn, ms, deps) {
  const fnRef = useRef(fn);
  fnRef.current = fn;
  useEffect(() => {
    let alive = true;
    let timer = null;
    const tick = async () => {
      if (!alive) return;
      if (visible()) await fnRef.current().catch(() => {});
      if (alive) timer = setTimeout(tick, ms);
    };
    tick();
    const onVis = () => { if (visible()) { clearTimeout(timer); tick(); } };
    document.addEventListener('visibilitychange', onVis);
    return () => { alive = false; clearTimeout(timer); document.removeEventListener('visibilitychange', onVis); };
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
}

// Dictation into the composer with the browser's recognizer (phone-friendly);
// hidden where the browser has none. Nothing leaves the page.
function useDictation(onText) {
  const Rec = typeof window !== 'undefined' ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null;
  const recRef = useRef(null);
  const [on, setOn] = useState(false);
  const stop = useCallback(() => { try { recRef.current?.stop(); } catch { /* ignore */ } setOn(false); }, []);
  const start = useCallback(() => {
    if (!Rec) return;
    const r = new Rec();
    r.continuous = true; r.interimResults = false; r.lang = navigator.language || 'en-US';
    r.onresult = (e) => { for (let i = e.resultIndex; i < e.results.length; i += 1) if (e.results[i].isFinal) onText(e.results[i][0].transcript); };
    r.onend = () => setOn(false);
    r.onerror = () => setOn(false);
    recRef.current = r;
    try { r.start(); setOn(true); } catch { setOn(false); }
  }, [Rec, onText]);
  useEffect(() => () => { try { recRef.current?.stop(); } catch { /* ignore */ } }, []);
  return { supported: !!Rec, on, start, stop };
}

const Md = ({ children }) => <ReactMarkdown remarkPlugins={[remarkGfm]}>{String(children || '')}</ReactMarkdown>;

// ---- tab strip -------------------------------------------------------------------
function Badge({ pulse, kind }) {
  if (kind === 'builder') return <span className="ws-badge ws-badge-stage">{pulse?.stage || 'lab'}</span>;
  if (!pulse) return null;
  if (pulse.needsUser) return <span className="ws-badge ws-badge-warn" title="waiting on you">{pulse.needsUser}</span>;
  if (pulse.live) return <span className="ws-badge ws-badge-live" title="sessions running">{pulse.live}</span>;
  if (pulse.blocked || pulse.stalled) return <span className="ws-badge ws-badge-danger" title="blocked or stalled">!</span>;
  return null;
}

function Tab({ s, active, onPick }) {
  const ref = useRef(null);
  useEffect(() => { if (active) ref.current?.scrollIntoView({ inline: 'nearest', block: 'nearest' }); }, [active]);
  const dot = s.kind === 'builder' ? 'var(--info)' : s.pulse?.up === true ? 'var(--ok)' : s.pulse?.up === false ? 'var(--danger)' : 'var(--muted)';
  return (
    <button
      ref={ref}
      type="button"
      className={`ws-tab ${active ? 'active' : ''}`}
      style={s.color ? { '--ws-accent': s.color } : undefined}
      onClick={() => onPick(s.id)}
      role="tab"
      aria-selected={active}
      title={`${s.org?.name} · ${KIND_LABEL[s.kind] || s.kind}${s.lane ? ` · ${s.lane}` : ''}${s.repoId ? ` · ${s.repoId}` : ''}`}
    >
      <span className="ws-tab-dot" style={{ background: dot }} aria-hidden="true" />
      <span className="ws-tab-text">
        <span className="ws-tab-label">{s.label}</span>
        <span className="ws-tab-org">{s.org?.name}{s.kind === 'lane' ? ` ⫽ ${s.lane}` : s.kind === 'repo' ? ` · ${s.repoId}` : ''}</span>
      </span>
      <Badge pulse={s.pulse} kind={s.kind} />
    </button>
  );
}

// ---- the stream menu (rename · colour · pin · hide) ----------------------------------
function StreamMenu({ s, onSave, onRemove, onAddLane, onAddRepo, onClose }) {
  const [label, setLabel] = useState(s.label || '');
  const [lane, setLane] = useState('');
  const [repos, setRepos] = useState(null);
  const canAdd = s.kind === 'instance';
  useEffect(() => {
    if (!canAdd) return;
    apiPost(`/streams/${encodeURIComponent(s.id)}/dev`, { op: 'repos' }).then((r) => setRepos(r?.result?.repos || [])).catch(() => setRepos([]));
  }, [s.id, canAdd]);
  return (
    <div className="ws-menu" role="dialog" aria-label={`Stream options for ${s.label}`}>
      <label className="ws-menu-row">
        <span>Name</span>
        <input className="cs-input" value={label} onChange={(e) => setLabel(e.target.value)} maxLength={60} />
      </label>
      <div className="ws-menu-row">
        <span>Colour</span>
        <div className="ws-swatches">
          {COLORS.map((c) => (
            <button key={c} type="button" className={`ws-swatch ${s.color === c ? 'on' : ''}`} style={{ background: c }} aria-label={`colour ${c}`} onClick={() => onSave({ color: c })} />
          ))}
          <button type="button" className={`ws-swatch ws-swatch-none ${!s.color ? 'on' : ''}`} aria-label="no colour" onClick={() => onSave({ color: null })}>–</button>
        </div>
      </div>
      <div className="ws-menu-actions">
        <button type="button" className="cs-btn cs-btn-sm cs-btn-primary" onClick={() => onSave({ label })}>Save name</button>
        <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" onClick={() => onSave({ order: -Date.now() })}>Pin to front</button>
        <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" onClick={onRemove}>{s.kind === 'lane' || s.kind === 'repo' ? 'Remove' : 'Hide'}</button>
      </div>
      {canAdd && (
        <>
          <div className="ws-menu-row ws-menu-add">
            <span>New lane</span>
            <input className="cs-input" value={lane} placeholder="e.g. billing, onboarding v2" onChange={(e) => setLane(e.target.value)} maxLength={40} />
            <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" disabled={!lane.trim()} onClick={() => onAddLane(lane.trim())}>Add</button>
          </div>
          {repos && repos.length > 1 && (
            <div className="ws-menu-row ws-menu-add">
              <span>Repo as a stream</span>
              <div className="ws-repo-list">
                {repos.map((r) => <button key={r.id} type="button" className="cs-btn cs-btn-sm cs-btn-outline" onClick={() => onAddRepo(r.id, r.label || r.id)}>{r.label || r.id}</button>)}
              </div>
            </div>
          )}
        </>
      )}
      <button type="button" className="ws-menu-close" onClick={onClose} aria-label="Close">✕</button>
    </div>
  );
}

// ---- the pane: one stream's Dev Console ---------------------------------------------
function Questions({ ev, busy, onAnswer }) {
  const [answers, setAnswers] = useState({});
  const qs = ev.questions || [];
  return (
    <div className="ws-card ws-questions" data-nova-note="The agent asked these before continuing; answer and send, or leave blank for its recommendation.">
      <div className="ws-card-head">Questions from the agent</div>
      {qs.map((q) => (
        <div key={q.id} className="ws-q">
          <div className="ws-q-text">{q.question}</div>
          {Array.isArray(q.options) && q.options.length > 0 ? (
            <div className="ws-q-opts">
              {q.options.map((o) => {
                const val = typeof o === 'string' ? o : (o.label || o.id);
                const on = answers[q.id] === val;
                return <button key={val} type="button" className={`ws-opt ${on ? 'on' : ''}`} onClick={() => setAnswers((a) => ({ ...a, [q.id]: val }))}>{val}</button>;
              })}
            </div>
          ) : (
            <input className="cs-input" value={answers[q.id] || ''} placeholder={q.recommended ? `recommended: ${q.recommended}` : 'your answer'} onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))} />
          )}
        </div>
      ))}
      <button type="button" className="cs-btn cs-btn-sm cs-btn-primary" disabled={busy} onClick={() => onAnswer(answers)}>Send answers</button>
    </div>
  );
}

function Event({ e }) {
  switch (e.type) {
    case 'user': return <div className="ws-ev ws-ev-user"><Md>{e.text}</Md></div>;
    case 'assistant': return <div className="ws-ev ws-ev-assistant"><Md>{e.text}</Md></div>;
    case 'thinking': return e.text ? <div className="ws-ev ws-ev-thought">{clip(e.text, 240)}</div> : null;
    case 'plan': return (
      <div className="ws-ev ws-ev-plan">
        <div className="ws-ev-kicker">Plan</div>
        {e.error ? <div className="ws-ev-error">{e.error}</div> : <Md>{e.text}</Md>}
      </div>
    );
    case 'tool': return <div className="ws-ev ws-ev-tool"><span className="ws-ev-kicker">{e.name || 'tool'}</span> <span className="ws-ev-args">{clip(e.args, 160)}</span></div>;
    case 'status': return e.message ? <div className={`ws-ev ws-ev-status ${e.status || ''}`}>{e.message}</div> : null;
    case 'result': return (
      <div className={`ws-ev ws-ev-result ${e.status === 'error' ? 'error' : ''}`}>
        <div className="ws-ev-kicker">{e.status === 'error' ? 'Failed' : 'Result'}</div>
        {e.error ? <div className="ws-ev-error">{e.error}</div> : <Md>{e.text}</Md>}
      </div>
    );
    default: return null;
  }
}

function ShipStrip({ ship, busy, onShip }) {
  if (!ship) return null;
  const steps = ship.steps || [];
  return (
    <div className="ws-ship" data-nova-note="The Ship strip: where this change is on its way to UAT/production; the button runs the next step.">
      <div className="ws-ship-steps">
        {steps.map((st) => <span key={st.id} className={`ws-step ${st.status || ''} ${st.stalled ? 'stalled' : ''}`} title={st.reason || st.status}>{st.label}</span>)}
      </div>
      <div className="ws-ship-next">
        {ship.running ? <span className="ws-muted">{ship.stalled ? 'stalled — ' : ''}running{ship.jobId ? ` · job ${String(ship.jobId).slice(0, 8)}` : ''}</span>
          : ship.next?.action ? (
            <button type="button" className="cs-btn cs-btn-sm cs-btn-primary" disabled={busy || ship.next.disabled} title={ship.next.reason || ''} onClick={() => onShip(ship.next)} data-agent-guard="ship">
              {ship.next.label || `Ship${ship.env ? ` → ${ship.env}` : ''}`}
            </button>
          ) : <span className="ws-muted">{ship.reason || 'nothing to ship'}</span>}
        {ship.prUrl && <a className="ws-link" href={ship.prUrl} target="_blank" rel="noopener noreferrer">PR ↗</a>}
      </div>
    </div>
  );
}

function BuilderPane({ s }) {
  const to = `/orgs/${s.orgId}/builder?app=${encodeURIComponent(s.builderAppId)}`;
  return (
    <div className="ws-pane" data-nova-note={`App Builder stream "${s.label}" in ${s.org?.name}: designed, not hosted yet. The next step is in the App Builder.`}>
      <div className="ws-pulse">
        <span className="ws-chip">{s.org?.name}</span>
        <span className="ws-chip ws-chip-kind">App Builder · {s.pulse?.stage || 'lab'}</span>
      </div>
      <div className="ws-card">
        <div className="ws-card-head">Not hosted yet</div>
        {s.pulse?.next ? <p><strong>{s.pulse.next.label}</strong> — {s.pulse.next.text}</p> : <p>Design it in App Builder; when an instance launches, this tab becomes its Dev Console.</p>}
        <Link className="cs-btn cs-btn-sm cs-btn-primary" to={to}>Open in App Builder →</Link>
      </div>
    </div>
  );
}

function StreamPane({ s, onPulse }) {
  const location = useLocation();
  const [sessions, setSessions] = useState(null);
  const [selected, setSelected] = useState(() => new URLSearchParams(location.search).get('session') || null);
  const [feed, setFeed] = useState({ session: null, events: [], lastSeq: -1 });
  const [ship, setShip] = useState(null);
  const [showList, setShowList] = useState(false);
  const [fresh, setFresh] = useState(false); // compose a NEW session
  const [draft, setDraft] = useState('');
  const [mode, setMode] = useState('auto');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [kitOld, setKitOld] = useState(false);
  const feedRef = useRef(null);
  const selectedRef = useRef(selected);
  selectedRef.current = selected;

  const dev = useCallback(async (op, args = {}) => {
    const r = await apiPost(`/streams/${encodeURIComponent(s.id)}/dev`, { op, ...args });
    if (r?.needsKitUpdate) { setKitOld(true); throw Object.assign(new Error(r.error || 'kit too old'), { kitOld: true }); }
    return r?.result;
  }, [s.id]);

  // Sessions list: 3 s while something runs, 20 s otherwise.
  const anyLive = (sessions || []).some((x) => LIVE.has(x.status));
  useVisiblePoll(async () => {
    const rows = await dev('list', { limit: 15 });
    setSessions(rows);
    setErr(null);
    if (!selectedRef.current && !fresh) {
      const pinned = (s.pinnedSessionIds || []).find((id) => rows.some((r) => r.id === id));
      const first = pinned || rows[0]?.id || null;
      if (first) setSelected(first);
    }
  }, anyLive ? 3000 : 20000, [s.id, anyLive, fresh]);

  // The selected session's feed, appended by seq.
  useEffect(() => { setFeed({ session: null, events: [], lastSeq: -1 }); setShip(null); }, [selected]);
  const feedLive = LIVE.has(feed.session?.status);
  useVisiblePoll(async () => {
    if (!selected) return;
    const r = await dev('session', { sessionId: selected, after: feed.lastSeq, limit: 200 });
    setFeed((f) => {
      const seen = new Set(f.events.map((e) => e.id));
      const add = (r.events || []).filter((e) => !seen.has(e.id));
      return { session: r.session, events: [...f.events, ...add].slice(-400), lastSeq: r.lastSeq ?? f.lastSeq };
    });
  }, feedLive ? 3000 : 15000, [s.id, selected, feedLive]);

  // Ship status follows the session once it has a result or a PR.
  const wantShip = !!feed.session && (feed.session.prUrl || feed.session.shipNext || feed.session.status === 'done');
  useVisiblePoll(async () => {
    if (!selected || !wantShip) return;
    setShip(await dev('shipStatus', { sessionId: selected }));
  }, feed.session?.shipRunning ? 5000 : 30000, [s.id, selected, wantShip, feed.session?.shipRunning]);

  useEffect(() => { if (feedRef.current) feedRef.current.scrollTop = feedRef.current.scrollHeight; }, [feed.events.length, selected]);
  useEffect(() => { onPulse?.(s.id, feed.session); }, [feed.session?.status, feed.session?.phase]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (fn) => {
    if (busy) return;
    setBusy(true); setErr(null);
    try { await fn(); } catch (e) { if (!e.kitOld) setErr(e.message); } finally { setBusy(false); }
  };
  const send = () => act(async () => {
    const prompt = draft.trim();
    if (!prompt) return;
    if (fresh || !selected) {
      const out = await dev('create', { prompt, mode, ...(s.repoId ? { repoId: s.repoId } : {}) });
      setDraft(''); setFresh(false); setSelected(out.id || out.sessionId);
    } else {
      await dev('message', { sessionId: selected, prompt, ...(mode !== 'auto' ? { mode } : {}) });
      setDraft('');
    }
  });
  const dictation = useDictation(useCallback((t) => setDraft((d) => `${d}${d && !d.endsWith(' ') ? ' ' : ''}${t}`), []));

  const sess = feed.session;
  const openQuestions = useMemo(() => {
    if (!sess?.awaitingAnswers) return null;
    return [...feed.events].reverse().find((e) => e.type === 'questions') || null;
  }, [feed.events, sess?.awaitingAnswers]);
  const canApprove = sess && sess.mode === 'plan' && !sess.planExecuted && !sess.awaitingAnswers && !LIVE.has(sess.status);

  const sso = () => act(async () => {
    const r = await apiPost(`/streams/${encodeURIComponent(s.id)}/sso`, selected ? { sessionId: selected } : {});
    if (r?.url) window.open(r.url, '_blank', 'noopener');
  });

  const p = s.pulse || {};
  const note = `Stream "${s.label}" (${KIND_LABEL[s.kind]}) on ${s.name} in ${s.org?.name}. ${p.live || 0} running, ${p.needsUser || 0} waiting on you; ` +
    `${sess ? `showing session "${sess.title || sess.id}" (${sess.status}${sess.phase ? `, ${sess.phase}` : ''}).` : 'no session selected.'} The composer sends to this session; "New" starts another.`;

  return (
    <div className="ws-pane" data-nova-note={note}>
      <div className="ws-pulse">
        <span className="ws-chip">{s.org?.name}</span>
        <span className="ws-chip ws-chip-kind">{KIND_LABEL[s.kind]}{s.lane ? ` ⫽ ${s.lane}` : ''}{s.repoId && s.kind !== 'lane' ? ` · ${s.repoId}` : ''}</span>
        <span className={`ws-chip ${p.up === true ? 'ok' : p.up === false ? 'danger' : ''}`}>{p.up === true ? 'up' : p.up === false ? p.status || 'down' : 'no report'}</span>
        {p.live > 0 && <span className="ws-chip info">{p.live} running</span>}
        {p.needsUser > 0 && <span className="ws-chip warn">{p.needsUser} waiting on you</span>}
        {p.kitRef && <span className="ws-chip" title="kit version">{String(p.kitRef).slice(0, 20)}</span>}
        <span className="ws-pulse-spacer" />
        <button type="button" className="ws-link ws-sso" onClick={sso} disabled={busy} title="Sign in to this instance's full Dev Console (90-second link)">Open full console ↗</button>
      </div>

      {kitOld && (
        <div className="ws-banner warn">
          This instance runs a kit without work streams yet. Update it (Operate → Updates) or <button type="button" className="ws-link" onClick={sso}>open the full console ↗</button>.
        </div>
      )}
      {err && <div className="ws-banner danger">{err}</div>}

      <div className="ws-session-bar">
        <button type="button" className="ws-session-pick" onClick={() => setShowList((v) => !v)} aria-expanded={showList}>
          <span className={`ws-status ${sess?.status || ''}`} />
          <span className="ws-session-title">{fresh ? 'New session' : sess?.title || (sessions && !sessions.length ? 'No sessions yet' : selected ? 'Loading…' : 'Pick a session')}</span>
          <span className="ws-session-meta">{sess ? `${sess.status}${sess.phase ? ` · ${sess.phase}` : ''} · ${fmtAgo(sess.updatedAt)}` : ''}</span>
          <span aria-hidden="true">{showList ? '▴' : '▾'}</span>
        </button>
        <button type="button" className={`cs-btn cs-btn-sm ${fresh ? 'cs-btn-primary' : 'cs-btn-outline'}`} onClick={() => { setFresh((v) => !v); setShowList(false); }}>New</button>
      </div>
      {showList && (
        <div className="ws-sheet" role="listbox" aria-label="Sessions on this stream">
          {(sessions || []).map((r) => (
            <button key={r.id} type="button" role="option" aria-selected={r.id === selected} className={`ws-sheet-row ${r.id === selected ? 'on' : ''}`} onClick={() => { setSelected(r.id); setFresh(false); setShowList(false); }}>
              <span className={`ws-status ${r.status}`} />
              <span className="ws-sheet-title">{r.title || r.id}{r.lane && s.kind !== 'lane' ? <span className="ws-chip ws-chip-mini">⫽ {r.lane}</span> : null}</span>
              <span className="ws-session-meta">{r.status}{r.shipNext?.label ? ` · ${r.shipNext.label}` : ''} · {fmtAgo(r.updatedAt)}</span>
            </button>
          ))}
          {sessions && !sessions.length && <div className="ws-muted ws-sheet-empty">Nothing here yet — the composer below starts the first one.</div>}
        </div>
      )}

      {!fresh && (
        <div className="ws-feed" ref={feedRef}>
          {sess?.liveThought && LIVE.has(sess.status) && <div className="ws-ev ws-ev-thought">{sess.liveThought}</div>}
          {feed.events.map((e) => <Event key={e.id} e={e} />)}
          {openQuestions && <Questions ev={openQuestions} busy={busy} onAnswer={(answers) => act(() => dev('answer', { sessionId: selected, answers }))} />}
          {canApprove && (
            <div className="ws-approve">
              <button type="button" className="cs-btn cs-btn-sm cs-btn-primary" disabled={busy} onClick={() => act(() => dev('approve', { sessionId: selected }))} data-agent-guard="approve">Approve &amp; execute</button>
              <span className="ws-muted">runs the plan above on the repo</span>
            </div>
          )}
          {sess && LIVE.has(sess.status) && (
            <div className="ws-approve">
              <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" disabled={busy} onClick={() => act(() => dev('cancel', { sessionId: selected }))} data-agent-guard="cancel">Stop</button>
              <span className="ws-muted">{sess.lastTool ? `last tool ${sess.lastTool}` : 'working…'}</span>
            </div>
          )}
          {sess?.prUrl && <div className="ws-approve"><a className="ws-link" href={sess.prUrl} target="_blank" rel="noopener noreferrer">Pull request ↗</a>{sess.previewUrl && <a className="ws-link" href={sess.previewUrl} target="_blank" rel="noopener noreferrer">Preview ↗</a>}</div>}
          {!feed.events.length && selected && <div className="ws-muted ws-feed-empty">Loading the session…</div>}
          {!selected && sessions && !sessions.length && <div className="ws-muted ws-feed-empty">Ask for the first change below.</div>}
        </div>
      )}

      {!fresh && <ShipStrip ship={ship} busy={busy} onShip={(next) => act(() => dev('ship', { sessionId: selected, ...(next.env ? { env: next.env } : {}), ...(next.retry ? { retry: true } : {}) }))} />}

      <form className="ws-composer" onSubmit={(e) => { e.preventDefault(); send(); }}>
        <textarea
          className="cs-input ws-draft"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) send(); }}
          placeholder={fresh || !selected ? `Start a session on ${s.label}${s.lane ? ` ⫽ ${s.lane}` : ''}…` : 'Reply to this session… (⌘↵ to send)'}
          rows={2}
        />
        <div className="ws-composer-row">
          <select className="cs-input ws-mode" value={mode} onChange={(e) => setMode(e.target.value)} aria-label="Mode">
            <option value="auto">auto</option>
            <option value="plan">plan first</option>
            <option value="agent">just do it</option>
          </select>
          {dictation.supported && (
            <button type="button" className={`cs-btn cs-btn-sm cs-btn-outline ws-mic ${dictation.on ? 'on' : ''}`} onClick={() => (dictation.on ? dictation.stop() : dictation.start())} aria-pressed={dictation.on} title="Dictate">
              {dictation.on ? '● listening' : '🎙'}
            </button>
          )}
          <span className="ws-pulse-spacer" />
          <button type="submit" className="cs-btn cs-btn-sm cs-btn-primary" disabled={busy || !draft.trim()}>{fresh || !selected ? 'Start' : 'Send'}</button>
        </div>
      </form>
    </div>
  );
}

// ---- the page -----------------------------------------------------------------------
export default function Streams() {
  const location = useLocation();
  const navigate = useNavigate();
  const voice = useVoiceSessionOnly();
  const [data, setData] = useState(null);
  const [loadErr, setLoadErr] = useState(null);
  const [showHidden, setShowHidden] = useState(false);
  const [menu, setMenu] = useState(false);
  const wanted = new URLSearchParams(location.search).get('stream');
  const [activeId, setActiveId] = useState(() => wanted || (() => { try { return localStorage.getItem(LAST_STREAM); } catch { return null; } })());

  const load = useCallback(async () => {
    try { setData(await apiGet('/streams')); setLoadErr(null); } catch (e) { setLoadErr(e.message); }
  }, []);
  useVisiblePoll(load, 20000, [load]);

  const streams = useMemo(() => (data?.streams || []).filter((s) => showHidden || !s.hidden), [data, showHidden]);
  const active = streams.find((s) => s.id === activeId) || streams[0] || null;

  // ?stream= wins; the pick persists for the next visit.
  useEffect(() => { if (wanted && wanted !== activeId) setActiveId(wanted); }, [wanted]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!active) return;
    try { localStorage.setItem(LAST_STREAM, active.id); } catch { /* ignore */ }
    const q = new URLSearchParams(location.search);
    if (q.get('stream') !== active.id) { q.set('stream', active.id); q.delete('session'); navigate({ search: `?${q}` }, { replace: true }); }
  }, [active?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // The live voice conversation hears which stream is on screen and when
  // the shown session changes state — spoken feedback per stream, hands free.
  const narratedRef = useRef(new Map());
  useEffect(() => {
    if (!active || !voice.active) return;
    const p = active.pulse || {};
    voice.sendSystem(`Work Streams: the user is now looking at stream "${active.label}" (${KIND_LABEL[active.kind]}, ${active.org?.name}${active.lane ? `, lane ${active.lane}` : ''}; stream id ${active.id}). ${p.live || 0} sessions running, ${p.needsUser || 0} waiting on them${p.lastTitle ? `; latest: "${p.lastTitle}"` : ''}. stream_* tools act on this stream by id.`);
  }, [active?.id, voice.active]); // eslint-disable-line react-hooks/exhaustive-deps
  const onPulse = useCallback((streamId, session) => {
    if (!session || !voice.active) return;
    const key = `${streamId}:${session.id}`;
    const prev = narratedRef.current.get(key);
    const now = `${session.status}|${session.awaitingAnswers ? 'q' : ''}|${session.mode === 'plan' && !session.planExecuted ? 'plan' : ''}`;
    narratedRef.current.set(key, now);
    if (!prev || prev === now) return;
    const s = streams.find((x) => x.id === streamId);
    const what = session.awaitingAnswers ? 'is asking questions' : session.mode === 'plan' && !session.planExecuted && !LIVE.has(session.status) ? 'has a plan waiting for approval' : session.status === 'done' ? 'finished' : session.status === 'error' ? 'failed' : `is ${session.status}`;
    voice.sendSystem(`Stream "${s?.label || streamId}": session "${session.title || session.id}" ${what}. Tell the user in one sentence.`, { respond: true });
  }, [streams, voice]);

  const pick = (id) => { setActiveId(id); setMenu(false); };
  // Swipe between tabs on touch screens.
  const touch = useRef(null);
  const onTouchStart = (e) => { touch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; };
  const onTouchEnd = (e) => {
    const t = touch.current; touch.current = null;
    if (!t || !active) return;
    const dx = e.changedTouches[0].clientX - t.x; const dy = e.changedTouches[0].clientY - t.y;
    if (Math.abs(dx) < 70 || Math.abs(dy) > 60) return;
    const i = streams.findIndex((s) => s.id === active.id);
    const next = streams[dx < 0 ? i + 1 : i - 1];
    if (next) pick(next.id);
  };

  const save = async (patch) => { await apiPost('/streams', { id: active.id, ...patch }); await load(); };
  const remove = async () => { await apiPost(`/streams/${encodeURIComponent(active.id)}/remove`, {}); setMenu(false); setActiveId(null); await load(); };
  const addLane = async (lane) => { const r = await apiPost('/streams', { kind: 'lane', instanceStreamId: active.id, lane }); setMenu(false); await load(); setActiveId(r.id); };
  const addRepo = async (repoId, label) => { const r = await apiPost('/streams', { kind: 'repo', instanceStreamId: active.id, repoId, label }); setMenu(false); await load(); setActiveId(r.id); };

  if (!isBackendConfigured()) return <div className="cs-page ws-page"><p className="cs-help">The backend API is not configured.</p></div>;
  const hiddenCount = (data?.streams || []).filter((s) => s.hidden).length;

  return (
    <div className="ws-page" data-nova-note="Work Streams: one tab per parallel effort across every organization — tap a tab (or swipe) to drive that Dev Console; the ⋯ renames, colours, pins or hides a stream and adds lanes.">
      <div className="ws-strip" role="tablist" aria-label="Work streams">
        {streams.map((s) => <Tab key={s.id} s={s} active={active?.id === s.id} onPick={pick} />)}
        {data && !streams.length && <span className="ws-muted ws-strip-empty">No streams yet — a live hosted instance you may open the Dev Console on, or an App Builder app, becomes one.</span>}
      </div>
      {active && (
        <div className="ws-toolbar">
          <span className="ws-muted">{streams.length} stream{streams.length === 1 ? '' : 's'} · {data?.orgs?.length || 0} org{(data?.orgs?.length || 0) === 1 ? '' : 's'}</span>
          {hiddenCount > 0 && <button type="button" className="ws-link" onClick={() => setShowHidden((v) => !v)}>{showHidden ? 'hide hidden' : `${hiddenCount} hidden`}</button>}
          <span className="ws-pulse-spacer" />
          <button type="button" className="ws-more" onClick={() => setMenu((v) => !v)} aria-label="Stream options" aria-expanded={menu} title="Rename, colour, pin, hide · add a lane or repo stream">⋯</button>
        </div>
      )}
      {menu && active && <StreamMenu key={active.id} s={active} onSave={save} onRemove={remove} onAddLane={addLane} onAddRepo={addRepo} onClose={() => setMenu(false)} />}
      {loadErr && <div className="ws-banner danger">{loadErr}</div>}
      <div className="ws-body" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        {!data && !loadErr && <div className="ws-muted ws-feed-empty">Finding your streams…</div>}
        {active && (active.kind === 'builder' ? <BuilderPane key={active.id} s={active} /> : <StreamPane key={active.id} s={active} onPulse={onPulse} />)}
      </div>
    </div>
  );
}
